import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spinner, SpinnerSize } from '@fluentui/react';
import ReactECharts from 'echarts-for-react';
import { ArrowClockwise24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { AppState } from '../../../redux/reducers';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import {
    AddendumComparisonDto,
    ConceptTotalsDto,
    ContractStatisticsFilter,
    PatientTotalsDto,
    PagedApiResponse,
    StatisticsSummaryDto,
} from '../../../models/Entities/Contracts/Statistics/ContractStatisticsModels';
import * as ContractStatisticsService from '../../../services/Contract-Statistics.Services';

import './ContractStatistics.Styles.css';

interface OwnProps {
    ContractId: number;
    // eslint-disable-next-line react/no-unused-prop-types
    SelectedAddendumId?: number;
    Addendums?: ContractAddendum[];
}

const formatCurrency = (amount: number) => {
    try {
        return amount.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
    } catch {
        return `ARS ${amount.toFixed(2)}`;
    }
};

const SectionCard: React.FC<{ title: string; desc?: string; children: React.ReactNode }> = ({ title, desc, children }) => {
    return (
        <div className="cs-card">
            <div className="cs-card__header">
                <div className="cs-card__title">{title}</div>
                {desc && <div className="cs-card__desc">{desc}</div>}
            </div>
            <div className="cs-card__body">{children}</div>
        </div>
    );
};

const ContractDetailStatisticsComponent: React.FC<OwnProps> = ({
    ContractId,
    SelectedAddendumId,
    Addendums,
}) => {
    const [t] = useTranslation();
    const AccessToken = useSelector((state: AppState) => (state as any)?.oidc?.user?.access_token as string | undefined);

    const [summary, setSummary] = useState<StatisticsSummaryDto | null>(null);
    const [conceptTotalsDetailed, setConceptTotalsDetailed] = useState<ConceptTotalsDto[]>([]);
    const [patientTotals, setPatientTotals] = useState<PagedApiResponse<PatientTotalsDto> | null>(null);
    const [addendumComparisons, setAddendumComparisons] = useState<AddendumComparisonDto[]>([]);
    const [activeConceptKey, setActiveConceptKey] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [inputAddendumId, setInputAddendumId] = useState<number | undefined>(SelectedAddendumId);
    const [inputFrom, setInputFrom] = useState<string>('');
    const [inputTo, setInputTo] = useState<string>('');
    const [appliedFilter, setAppliedFilter] = useState<ContractStatisticsFilter>({
        addendumId: SelectedAddendumId,
    });

    useEffect(() => {
        if (SelectedAddendumId) {
            setInputAddendumId(SelectedAddendumId);
            setAppliedFilter({ addendumId: SelectedAddendumId });
        }
    }, [SelectedAddendumId]);

    const availableAddendums = useMemo(() => {
        return (Addendums || []).map((item) => ({
            id: item.id,
            name: item.contractAddendumName || `${t('contractStatistics.addendumLabel', 'Adenda')} ${item.id}`,
        }));
    }, [Addendums, t]);

    const comparisonAddendumIds = useMemo(() => {
        if (appliedFilter.addendumId) {
            return [appliedFilter.addendumId];
        }

        return availableAddendums.map((item) => item.id);
    }, [appliedFilter.addendumId, availableAddendums]);

    const conceptChartOption = useMemo(() => {
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (items: any) => {
                    const entry = items?.[0];
                    return `${entry?.name}: ${formatCurrency(Number(entry?.value || 0))}`;
                },
            },
            grid: { left: 10, right: 24, bottom: 30, top: 20, containLabel: true },
            xAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value: number) => `${Math.round(value / 1000)}k`,
                },
            },
            yAxis: {
                type: 'category',
                data: conceptTotalsDetailed.map((item) => item.conceptName || `#${item.conceptId}`),
                axisLabel: {
                    interval: 0,
                    width: 200,
                    overflow: 'truncate',
                },
            },
            series: [
                {
                    type: 'bar',
                    data: conceptTotalsDetailed.map((item) => item.totalAmount),
                    itemStyle: { color: '#0078d4' },
                    barMaxWidth: 18,
                },
            ],
        };
    }, [conceptTotalsDetailed]);

    const stageTotals = useMemo(() => {
        const map = new Map<number, { stageId: number; stageName: string; visits: number; amount: number }>();

        conceptTotalsDetailed.forEach((concept) => {
            (concept.stageBreakdowns || []).forEach((stage) => {
                const current = map.get(stage.stageId) || {
                    stageId: stage.stageId,
                    stageName: stage.stageName || `#${stage.stageId}`,
                    visits: 0,
                    amount: 0,
                };

                current.visits += stage.visits || 0;
                current.amount += stage.amount || 0;
                map.set(stage.stageId, current);
            });
        });

        return Array.from(map.values()).sort((a, b) => b.amount - a.amount);
    }, [conceptTotalsDetailed]);

    const stageChartOption = useMemo(() => {
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (items: any) => {
                    const entry = items?.[0];
                    return `${entry?.name}: ${formatCurrency(Number(entry?.value || 0))}`;
                },
            },
            grid: { left: 10, right: 24, bottom: 30, top: 20, containLabel: true },
            xAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value: number) => `${Math.round(value / 1000)}k`,
                },
            },
            yAxis: {
                type: 'category',
                data: stageTotals.map((item) => item.stageName),
                axisLabel: {
                    interval: 0,
                    width: 180,
                    overflow: 'truncate',
                },
            },
            series: [
                {
                    type: 'bar',
                    data: stageTotals.map((item) => item.amount),
                    itemStyle: { color: '#005a9e' },
                    barMaxWidth: 18,
                },
            ],
        };
    }, [stageTotals]);

    const addendumChartOption = useMemo(() => {
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
            },
            grid: { left: 10, right: 24, bottom: 30, top: 24, containLabel: true },
            xAxis: {
                type: 'category',
                data: addendumComparisons.map((item) => item.addendumName || `#${item.contractAddendumId}`),
                axisLabel: {
                    interval: 0,
                    width: 180,
                    overflow: 'truncate',
                },
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value: number) => `${Math.round(value / 1000)}k`,
                },
            },
            series: [
                {
                    type: 'bar',
                    data: addendumComparisons.map((item) => item.totalAmount),
                    itemStyle: { color: '#107c10' },
                    barMaxWidth: 26,
                },
            ],
        };
    }, [addendumComparisons]);

    const activeConcept = useMemo(() => {
        if (!activeConceptKey) {
            return null;
        }

        return conceptTotalsDetailed.find((item) => `${item.conceptId}-${item.contractAddendumId}` === activeConceptKey) || null;
    }, [activeConceptKey, conceptTotalsDetailed]);

    const loadStatistics = useCallback(async () => {
        if (!AccessToken || !ContractId) {
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        try {
            const comparePromise = comparisonAddendumIds.length > 0
                ? ContractStatisticsService.GetCompareByAddendum(ContractId, AccessToken, comparisonAddendumIds)
                : Promise.resolve([] as AddendumComparisonDto[]);

            const [summaryResult, conceptResult, patientResult, compareResult] = await Promise.allSettled([
                ContractStatisticsService.GetSummary(ContractId, AccessToken),
                ContractStatisticsService.GetTotalsByConceptDetailed(ContractId, AccessToken, appliedFilter),
                ContractStatisticsService.GetTotalsByPatient(ContractId, AccessToken, {
                    ...appliedFilter,
                    page: 1,
                    pageSize: 25,
                }),
                comparePromise,
            ]);

            const nextSummary = summaryResult.status === 'fulfilled' ? summaryResult.value : null;
            const nextConcepts = conceptResult.status === 'fulfilled' ? conceptResult.value : [];
            const nextPatients = patientResult.status === 'fulfilled' ? patientResult.value : null;
            const nextComparisons = compareResult.status === 'fulfilled' ? compareResult.value : [];

            setSummary(nextSummary);
            setConceptTotalsDetailed(nextConcepts);
            setPatientTotals(nextPatients);
            setAddendumComparisons(nextComparisons);
            setActiveConceptKey(nextConcepts.length > 0 ? `${nextConcepts[0].conceptId}-${nextConcepts[0].contractAddendumId}` : null);

            const hasAnyData =
                !!nextSummary ||
                nextConcepts.length > 0 ||
                (nextPatients?.data || []).length > 0 ||
                nextComparisons.length > 0;

            if (!hasAnyData) {
                setErrorMessage(t('contractStatistics.errors.load', 'No se pudieron cargar las estadísticas del contrato.'));
            }
        } catch (error: any) {
            setErrorMessage(error?.ErrorMessage || t('contractStatistics.errors.load', 'No se pudieron cargar las estadísticas del contrato.'));
        } finally {
            setIsLoading(false);
        }
    }, [AccessToken, ContractId, appliedFilter, comparisonAddendumIds, t]);

    useEffect(() => {
        loadStatistics();
    }, [loadStatistics]);

    const applyFilters = () => {
        setAppliedFilter({
            addendumId: inputAddendumId,
            from: inputFrom || undefined,
            to: inputTo || undefined,
        });
    };

    const clearFilters = () => {
        setInputAddendumId(undefined);
        setInputFrom('');
        setInputTo('');
        setAppliedFilter({});
    };

    const topConcepts = summary?.topConcepts || [];

    return (
        <div className="cs-container">
            <div className="cs-hero">
                <div className="cs-hero__header">
                    <h3 className="cs-hero__title">{t('contractStatistics.title', 'Estadísticas del contrato')}</h3>
                    <span className="cs-hero__subtitle">
                        {t('contractStatistics.subtitle', 'Resumen económico y distribución por concepto, paciente y adenda.')}
                    </span>
                </div>

                <div className="cs-hero__body">
                    <div className="cs-hero__kpi">
                        <span className="cs-hero__kpi-label">{t('contractStatistics.kpi.total', 'Valor total')}</span>
                        <span className="cs-hero__kpi-value">{formatCurrency(summary?.totalContractValue || 0)}</span>
                    </div>
                    <div className="cs-hero__kpi">
                        <span className="cs-hero__kpi-label">{t('contractStatistics.kpi.average', 'Promedio por paciente')}</span>
                        <span className="cs-hero__kpi-value">{formatCurrency(summary?.avgPerPatient || 0)}</span>
                    </div>
                    <div className="cs-hero__kpi">
                        <span className="cs-hero__kpi-label">{t('contractStatistics.kpi.patients', 'Pacientes')}</span>
                        <span className="cs-hero__kpi-value">{summary?.patientCount || 0}</span>
                    </div>
                    <div className="cs-hero__kpi">
                        <span className="cs-hero__kpi-label">{t('contractStatistics.kpi.ratio', 'Ratio fijo / visita')}</span>
                        <span className="cs-hero__kpi-value">{(summary?.fixedVsVisitRatio || 0).toFixed(2)}</span>
                    </div>
                    <div className="cs-hero__kpi">
                        <button className="cs-cf-currency-btn cs-cf-currency-btn--active" onClick={loadStatistics}>
                            <ArrowClockwise24Regular />
                            <span>{t('contractStatistics.actions.refresh', 'Actualizar')}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="cs-content">
                <SectionCard
                    title={t('contractStatistics.filters.title', 'Filtros')}
                    desc={t('contractStatistics.filters.subtitle', 'Podés filtrar estadísticas por adenda y rango de fechas.')}
                >
                    <div className="cs-contract-filters-row">
                        <div className="cs-contract-filter-item">
                            <span className="cs-contract-filter-label">
                                {t('contractStatistics.filters.addendum', 'Adenda')}
                            </span>
                            <select
                                className="cs-contract-filter-input"
                                value={inputAddendumId || ''}
                                title={t('contractStatistics.filters.addendum', 'Adenda')}
                                aria-label={t('contractStatistics.filters.addendum', 'Adenda')}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setInputAddendumId(value ? Number(value) : undefined);
                                }}
                            >
                                <option value="">{t('contractStatistics.filters.allAddendums', 'Todas las adendas')}</option>
                                {availableAddendums.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="cs-contract-filter-item">
                            <span className="cs-contract-filter-label">
                                {t('contractStatistics.filters.from', 'Desde')}
                            </span>
                            <input
                                type="date"
                                className="cs-contract-filter-input"
                                value={inputFrom}
                                title={t('contractStatistics.filters.from', 'Desde')}
                                aria-label={t('contractStatistics.filters.from', 'Desde')}
                                onChange={(event) => setInputFrom(event.target.value)}
                            />
                        </div>
                        <div className="cs-contract-filter-item">
                            <span className="cs-contract-filter-label">
                                {t('contractStatistics.filters.to', 'Hasta')}
                            </span>
                            <input
                                type="date"
                                className="cs-contract-filter-input"
                                value={inputTo}
                                title={t('contractStatistics.filters.to', 'Hasta')}
                                aria-label={t('contractStatistics.filters.to', 'Hasta')}
                                onChange={(event) => setInputTo(event.target.value)}
                            />
                        </div>
                        <div className="cs-contract-filter-actions">
                            <button className="cs-cf-currency-btn cs-cf-currency-btn--active" onClick={applyFilters}>
                                {t('contractStatistics.actions.apply', 'Aplicar')}
                            </button>
                            <button className="cs-cf-currency-btn" onClick={clearFilters}>
                                {t('contractStatistics.actions.clear', 'Limpiar')}
                            </button>
                        </div>
                    </div>
                </SectionCard>

                {isLoading && (
                    <div className="cs-loading">
                        <Spinner size={SpinnerSize.medium} />
                        <span>{t('contractStatistics.loading', 'Cargando estadísticas...')}</span>
                    </div>
                )}

                {!isLoading && errorMessage && (
                    <div className="cs-empty">
                        <span>{errorMessage}</span>
                    </div>
                )}

                {!isLoading && !errorMessage && (
                    <>
                        <SectionCard
                            title={t('contractStatistics.sections.concepts', 'Totales por concepto (detallado)')}
                            desc={t('contractStatistics.sections.conceptsDesc', 'Nueva vista usando el desglose por etapa para cada concepto.')}
                        >
                            {conceptTotalsDetailed.length === 0 ? (
                                <div className="cs-empty">{t('contractStatistics.empty.concepts', 'No hay conceptos para el filtro seleccionado.')}</div>
                            ) : (
                                <>
                                    <ReactECharts option={conceptChartOption} style={{ height: Math.max(240, conceptTotalsDetailed.length * 28) }} opts={{ renderer: 'svg' }} />
                                    <div className="cs-table-wrap">
                                        <table className="cs-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('contractStatistics.table.concept', 'Concepto')}</th>
                                                    <th>{t('contractStatistics.table.visits', 'Visitas')}</th>
                                                    <th>{t('contractStatistics.table.stages', 'Etapas')}</th>
                                                    <th className="cs-table__amount">{t('contractStatistics.table.total', 'Total')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {conceptTotalsDetailed.map((item) => {
                                                    const rowKey = `${item.conceptId}-${item.contractAddendumId}`;
                                                    const isSelected = rowKey === activeConceptKey;

                                                    return (
                                                    <tr
                                                        key={rowKey}
                                                        className={`cs-table__interactive ${isSelected ? 'cs-table__interactive--selected' : ''}`}
                                                        onClick={() => setActiveConceptKey(rowKey)}
                                                    >
                                                        <td>{item.conceptName || `#${item.conceptId}`}</td>
                                                        <td>{item.totalVisits}</td>
                                                        <td>{(item.stageBreakdowns || []).length}</td>
                                                        <td className="cs-table__amount">{formatCurrency(item.totalAmount)}</td>
                                                    </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </SectionCard>

                        <div className="cs-two-col">
                            <SectionCard
                                title={t('contractStatistics.sections.stageBreakdown', 'Desglose por etapa')}
                                desc={t('contractStatistics.sections.stageBreakdownDesc', 'Acumulado de montos por etapa usando el endpoint detallado.')}
                            >
                                {stageTotals.length === 0 ? (
                                    <div className="cs-empty">{t('contractStatistics.empty.stages', 'No hay detalle por etapas para el filtro seleccionado.')}</div>
                                ) : (
                                    <>
                                        <ReactECharts option={stageChartOption} style={{ height: Math.max(220, stageTotals.length * 28) }} opts={{ renderer: 'svg' }} />
                                        <div className="cs-table-wrap">
                                            <table className="cs-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('contractStatistics.table.stage', 'Etapa')}</th>
                                                        <th>{t('contractStatistics.table.visits', 'Visitas')}</th>
                                                        <th className="cs-table__amount">{t('contractStatistics.table.total', 'Total')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {stageTotals.map((stage) => (
                                                        <tr key={stage.stageId}>
                                                            <td>{stage.stageName}</td>
                                                            <td>{stage.visits}</td>
                                                            <td className="cs-table__amount">{formatCurrency(stage.amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </SectionCard>

                            <SectionCard
                                title={t('contractStatistics.sections.patients', 'Totales por paciente')}
                                desc={t('contractStatistics.sections.patientsDesc', 'Pacientes ordenados por mayor monto acumulado.')}
                            >
                                {(patientTotals?.data || []).length === 0 ? (
                                    <div className="cs-empty">{t('contractStatistics.empty.patients', 'No hay pacientes para el filtro seleccionado.')}</div>
                                ) : (
                                    <div className="cs-table-wrap">
                                        <table className="cs-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('contractStatistics.table.patient', 'Paciente')}</th>
                                                    <th className="cs-table__amount">{t('contractStatistics.table.total', 'Total')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(patientTotals?.data || []).map((item) => (
                                                    <tr key={item.patientId || item.patientDisplayName || 'unknown'}>
                                                        <td>{item.patientDisplayName || item.patientId || '-'}</td>
                                                        <td className="cs-table__amount">{formatCurrency(item.totalAmount)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <div className="cs-contract-footnote">
                                            {t('contractStatistics.pagination', 'Mostrando {{shown}} de {{total}} registros', {
                                                shown: (patientTotals?.data || []).length,
                                                total: patientTotals?.totalRecords || (patientTotals?.data || []).length,
                                            })}
                                        </div>
                                    </div>
                                )}
                            </SectionCard>
                        </div>

                        <div className="cs-two-col">
                            <SectionCard
                                title={t('contractStatistics.sections.conceptStages', 'Detalle por etapas del concepto seleccionado')}
                                desc={t('contractStatistics.sections.conceptStagesDesc', 'Seleccioná un concepto en la tabla para ver su distribución por etapas.')}
                            >
                                {!activeConcept || (activeConcept.stageBreakdowns || []).length === 0 ? (
                                    <div className="cs-empty">{t('contractStatistics.empty.conceptStageBreakdown', 'No hay detalle de etapas para el concepto seleccionado.')}</div>
                                ) : (
                                    <>
                                        <div className="cs-contract-footnote">
                                            {t('contractStatistics.selectedConcept', 'Concepto seleccionado')}: <strong>{activeConcept.conceptName || `#${activeConcept.conceptId}`}</strong>
                                        </div>
                                        <div className="cs-table-wrap">
                                            <table className="cs-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('contractStatistics.table.stage', 'Etapa')}</th>
                                                        <th>{t('contractStatistics.table.visits', 'Visitas')}</th>
                                                        <th className="cs-table__amount">{t('contractStatistics.table.total', 'Total')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(activeConcept.stageBreakdowns || []).map((stage) => (
                                                        <tr key={`${activeConcept.conceptId}-${stage.stageId}`}>
                                                            <td>{stage.stageName || `#${stage.stageId}`}</td>
                                                            <td>{stage.visits}</td>
                                                            <td className="cs-table__amount">{formatCurrency(stage.amount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </SectionCard>

                            <SectionCard
                                title={t('contractStatistics.sections.addendums', 'Comparación por adenda')}
                                desc={t('contractStatistics.sections.addendumsDesc', 'Comparación de monto total entre adendas del contrato.')}
                            >
                                {addendumComparisons.length === 0 ? (
                                    <div className="cs-empty">{t('contractStatistics.empty.addendums', 'No hay datos de comparación por adenda.')}</div>
                                ) : (
                                    <>
                                        <ReactECharts option={addendumChartOption} style={{ height: 250 }} opts={{ renderer: 'svg' }} />
                                        <div className="cs-table-wrap">
                                            <table className="cs-table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('contractStatistics.table.addendum', 'Adenda')}</th>
                                                        <th className="cs-table__amount">{t('contractStatistics.table.total', 'Total')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {addendumComparisons.map((item) => (
                                                        <tr key={item.contractAddendumId}>
                                                            <td>{item.addendumName || `#${item.contractAddendumId}`}</td>
                                                            <td className="cs-table__amount">{formatCurrency(item.totalAmount)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </SectionCard>
                        </div>

                        <SectionCard
                            title={t('contractStatistics.sections.topConcepts', 'Top conceptos del resumen')}
                            desc={t('contractStatistics.sections.topConceptsDesc', 'Conceptos de mayor peso dentro del resumen general.')}
                        >
                            {topConcepts.length === 0 ? (
                                <div className="cs-empty">{t('contractStatistics.empty.topConcepts', 'No hay top conceptos disponibles.')}</div>
                            ) : (
                                <div className="cs-table-wrap">
                                    <table className="cs-table">
                                        <thead>
                                            <tr>
                                                <th>{t('contractStatistics.table.concept', 'Concepto')}</th>
                                                <th>{t('contractStatistics.table.visits', 'Visitas')}</th>
                                                <th className="cs-table__amount">{t('contractStatistics.table.total', 'Total')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topConcepts.map((item) => (
                                                <tr key={`top-${item.conceptId}`}>
                                                    <td>{item.conceptName || `#${item.conceptId}`}</td>
                                                    <td>{item.totalVisits}</td>
                                                    <td className="cs-table__amount">{formatCurrency(item.totalAmount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </SectionCard>
                    </>
                )}
            </div>
        </div>
    );
};

export const ContractDetailStatistics = ContractDetailStatisticsComponent;

export default ContractDetailStatistics;
